import { environmentConfig } from "@src/appV2/environment/config";
import { APP_V2_APP_EVENTS, logError } from "@src/appV2/lib/analytics";
import { useQuery } from "@tanstack/react-query";
// eslint-disable-next-line n/no-extraneous-import
import { compressToEncodedURIComponent, decompressFromEncodedURIComponent } from "lz-string";

import { getAuthHeaderValue } from "../Auth/cognito/getAuthorizationHeader";

export interface UseShiftFeedbackUrlProps {
  shiftId: string;
  clockInOut: {
    start?: string;
    end?: string;
  };
  lunchInOut?: {
    start?: string;
    end?: string;
  };
  breakAuthorizationReason?: string;
  facilityId: string;
  scannedByWorkerId?: string;
  token?: string;
  workerFirstName: string;
  isCaliforniaTimesheet: boolean;
}

interface ShiftFeedbackUrlResult {
  qrData: string;
  url: string;
}

export function compressShiftFeedbackData(props: UseShiftFeedbackUrlProps) {
  return compressToEncodedURIComponent(JSON.stringify(props));
}

export function decompressShiftFeedbackData(
  compressedData: string
): UseShiftFeedbackUrlProps | undefined {
  try {
    return JSON.parse(
      decompressFromEncodedURIComponent(compressedData)
    ) as UseShiftFeedbackUrlProps;
  } catch {
    return undefined;
  }
}

export function useShiftFeedbackUrl(props: UseShiftFeedbackUrlProps, enabled?: boolean) {
  return useQuery({
    queryKey: ["shift-feedback-url", props],
    queryFn: async (): Promise<ShiftFeedbackUrlResult> => {
      try {
        const authHeaderValue = await getAuthHeaderValue(true);
        const qrData = compressShiftFeedbackData({ ...props, token: authHeaderValue });
        const url = `${environmentConfig.REACT_APP_ADMIN_WEB_APP_URL}/shift-signout-feedback?data=${qrData}`;

        return { qrData, url };
      } catch (error) {
        logError(APP_V2_APP_EVENTS.SHIFT_FEEDBACK_URL_GENERATION_FAILED, {
          metadata: { ...props },
          error,
        });
        throw error;
      }
    },
    enabled,
    retry: 2,
    staleTime: 300_000,
  });
}
